/**
 * Progress Bar Component
 *
 * Configurable progress bar with color coding, labels,
 * and optional animation.
 *
 * @author BizClick WMS
 * @version 1.0.0
 */

import React from 'react';
import {
  getThresholdStatus,
  getThresholdStatusInverse,
  STATUS_COLORS,
  StatusLevel,
} from '../../theme/dashboardTheme';

interface ProgressBarProps {
  /** Current value */
  value: number;
  /** Maximum value (default: 100) */
  max?: number;
  /** Target value for threshold coloring */
  target?: number;
  /** Explicit color override (Tailwind bg class or hex) */
  color?: string;
  /** Status level override (uses theme colors) */
  status?: StatusLevel;
  /** Height variant */
  height?: 'xs' | 'sm' | 'md' | 'lg';
  /** Show percentage label */
  showLabel?: boolean;
  /** Show value label (e.g., "75/100") */
  showValue?: boolean;
  /** Unit for value display */
  unit?: string;
  /** Label text above the bar */
  label?: string;
  /** Whether lower values are better (inverse threshold) */
  inverse?: boolean;
  /** Enable animation on value change */
  animated?: boolean;
  /** Custom class name */
  className?: string;
}

const HEIGHT_MAP = {
  xs: 'h-1',
  sm: 'h-2',
  md: 'h-3',
  lg: 'h-4',
};

const ProgressBar: React.FC<ProgressBarProps> = ({
  value,
  max = 100,
  target,
  color,
  status: statusOverride,
  height = 'sm',
  showLabel = false,
  showValue = false,
  unit = '',
  label,
  inverse = false,
  animated = true,
  className = '',
}) => {
  const percent = Math.min(Math.max((value / max) * 100, 0), 100);

  // Determine color
  let barColor: string;
  if (color) {
    barColor = color;
  } else if (statusOverride) {
    barColor = STATUS_COLORS[statusOverride].bg;
  } else if (target !== undefined) {
    const statusLevel = inverse
      ? getThresholdStatusInverse(value, target)
      : getThresholdStatus(value, target);
    barColor = STATUS_COLORS[statusLevel].bg;
  } else {
    barColor = 'bg-blue-500';
  }

  return (
    <div className={`w-full ${className}`}>
      {/* Label row */}
      {(label || showLabel || showValue) && (
        <div className="flex items-center justify-between mb-1">
          {label && (
            <span className="text-xs text-gray-400 truncate">{label}</span>
          )}
          <div className="flex items-center gap-2 ml-auto">
            {showValue && (
              <span className="text-xs text-gray-400">
                {value.toLocaleString('nb-NO')}{unit && ` ${unit}`}
                {max !== 100 && ` / ${max.toLocaleString('nb-NO')}`}
              </span>
            )}
            {showLabel && (
              <span className="text-xs font-medium text-gray-300">
                {percent.toFixed(0)}%
              </span>
            )}
          </div>
        </div>
      )}

      {/* Bar */}
      <div className={`w-full ${HEIGHT_MAP[height]} bg-gray-700 rounded-full overflow-hidden`}>
        <div
          className={`
            ${HEIGHT_MAP[height]} ${barColor} rounded-full
            ${animated ? 'transition-all duration-700 ease-out' : ''}
          `}
          style={{ width: `${percent}%` }}
        />
      </div>
    </div>
  );
};

export default ProgressBar;
